import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { Package, CalendarClock, MapPin, Timer, CheckCircle2, Circle, Layers, Repeat, Upload, ClipboardList, AlertTriangle, BarChart3, Users, Settings, ExternalLink, Ship, Leaf, Download, BadgeCheck, XCircle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, ReferenceLine, PieChart, Pie } from "recharts";
import { BusinessReview, AccountStructure, OperationalRelated } from "@/components/SupplyChainExtra";

const TABS = [
  { key: "business-review", label: "Business Review", icon: BarChart3 },
  { key: "reports", label: "Status Report", icon: ClipboardList },
  { key: "tracking", label: "Tracking", icon: MapPin },
  { key: "schedules", label: "Schedules", icon: CalendarClock },
  { key: "orders", label: "Orders", icon: Package },
  { key: "account", label: "SGL Account Management Structure", icon: Users },
  { key: "operational", label: "Operational Related", icon: Settings },
  { key: "detention", label: "Detention Monitoring", icon: Timer },
  { key: "invoice", label: "Invoice Additional Charge Approval", icon: BadgeCheck },
];

const statusColor = (s) => {
  const v = String(s).toLowerCase();
  if (v.includes("delay") || v.includes("overdue") || v.includes("hold")) return "text-red-400 border-red-400/30";
  if (v.includes("transit") || v.includes("port") || v.includes("due")) return "text-amber-400 border-amber-400/30";
  if (v.includes("deliver") || v.includes("schedule") || v.includes("within") || v.includes("clear")) return "text-emerald-400 border-emerald-400/30";
  return "text-muted-foreground border-border";
};

const Badge = ({ children }) => (
  <span className={`inline-block font-mono-plex text-[10px] uppercase tracking-widest border px-2 py-0.5 ${statusColor(children)}`}>{children}</span>
);

const Table = ({ head, children }) => (
  <div className="border border-border overflow-x-auto">
    <table className="w-full text-sm min-w-[760px]">
      <thead>
        <tr className="bg-slate-50 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
          {head.map((h, i) => (
            <th key={i} className="text-left font-normal px-4 py-3 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const OrdersTable = ({ rows }) => (
  <Table head={["Booking #", "Consignee", "Origin", "Destination", "Containers", "Incoterm", "Status", "ETA"]}>
    {rows.map((r, i) => (
      <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors" data-testid={`order-row-${i}`}>
        <td className="px-4 py-3 font-mono-plex text-foreground">{r.po}</td>
        <td className="px-4 py-3">{r.supplier}</td>
        <td className="px-4 py-3 text-muted-foreground">{r.origin}</td>
        <td className="px-4 py-3 text-muted-foreground">{r.destination}</td>
        <td className="px-4 py-3 font-mono-plex">{r.containers}</td>
        <td className="px-4 py-3">{r.incoterm}</td>
        <td className="px-4 py-3"><Badge>{r.status}</Badge></td>
        <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.eta}</td>
      </tr>
    ))}
  </Table>
);

const SchedulesTable = ({ rows }) => (
  <Table head={["Vessel", "Voyage", "Service", "Origin", "ETD", "Destination", "ETA", "Status"]}>
    {rows.map((r, i) => (
      <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors" data-testid={`schedule-row-${i}`}>
        <td className="px-4 py-3 font-medium">{r.vessel}</td>
        <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.voyage}</td>
        <td className="px-4 py-3 font-mono-plex">{r.service}</td>
        <td className="px-4 py-3 text-muted-foreground">{r.origin}</td>
        <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.etd}</td>
        <td className="px-4 py-3 text-muted-foreground">{r.destination}</td>
        <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.eta}</td>
        <td className="px-4 py-3"><Badge>{r.status}</Badge></td>
      </tr>
    ))}
  </Table>
);

const Tracking = ({ rows }) => (
  <>
    <a
      href="https://www-sglprd.wisegrid.net/NEO/Desktop#/index"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="tracking-neo-link"
      className="group flex items-center justify-between gap-4 border border-border bg-white p-5 mb-6 hover:border-[#E3000F] transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 flex items-center justify-center bg-[#0B2B5C] text-white font-display text-lg tracking-tight rounded-md">NEO</div>
        <div>
          <div className="font-semibold text-sm">Track live in NEO</div>
          <div className="text-xs text-muted-foreground">Open SGL's NEO portal for real-time shipment visibility & milestones</div>
        </div>
      </div>
      <span className="inline-flex items-center gap-2 text-xs font-mono-plex tracking-widest uppercase text-[#E3000F]">
        Launch NEO <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </a>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {rows.map((r, i) => (
      <div key={i} className="border border-border bg-white p-6" data-testid={`tracking-card-${i}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono-plex text-sm text-foreground">{r.container}</div>
            <div className="text-xs text-muted-foreground mt-0.5">B/L {r.bl}</div>
          </div>
          <Badge>{r.status}</Badge>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{r.location}</span>
          <span>ETA {r.eta}</span>
        </div>
        <div className="h-1 w-full bg-secondary mb-5"><div className="h-full bg-[#F59E0B]" style={{ width: `${r.progress}%` }} /></div>
        <div className="space-y-2">
          {r.milestones.map((m, j) => (
            <div key={j} className="flex items-center gap-3 text-sm">
              {m.done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
              <span className={m.done ? "text-foreground" : "text-muted-foreground"}>{m.label}</span>
              <span className="ml-auto font-mono-plex text-xs text-muted-foreground">{m.date}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
    </div>
  </>
);

const parseMoney = (s) => Number(String(s).replace(/[^0-9.]/g, "")) || 0;

const Detention = ({ data }) => {
  const [mode, setMode] = useState("export");
  const rows = (data && data[mode]) || [];
  const enriched = rows.map((r) => {
    const remaining = r.free_days - r.days_used;
    let cat, color;
    if (remaining < 0) { cat = "On Detention"; color = "#EF4444"; }
    else if (remaining === 0) { cat = "Due Today"; color = "#F97316"; }
    else if (remaining <= 2) { cat = "Approaching"; color = "#F59E0B"; }
    else { cat = "Within Free Time"; color = "#10B981"; }
    return { ...r, remaining, cat, color };
  });
  const count = (c) => enriched.filter((e) => e.cat === c).length;
  const onDetention = count("On Detention");
  const dueToday = count("Due Today");
  const approaching = count("Approaching");
  const withinFree = count("Within Free Time");
  const atRisk = onDetention + dueToday + approaching;
  const totalCharges = enriched.reduce((s, e) => s + parseMoney(e.demurrage) + parseMoney(e.detention), 0);

  const kpis = [
    { label: "On Detention", value: onDetention, sub: "Charges accruing now", color: "#EF4444" },
    { label: "Due Today", value: dueToday, sub: "Last free day is today", color: "#F97316" },
    { label: "Approaching (≤2 days)", value: approaching, sub: "Act to avoid charges", color: "#F59E0B" },
    { label: "Within Free Time", value: withinFree, sub: "No action needed", color: "#10B981" },
  ];
  const breakdown = [
    { name: "On Detention", value: onDetention, color: "#EF4444" },
    { name: "Due Today", value: dueToday, color: "#F97316" },
    { name: "Approaching", value: approaching, color: "#F59E0B" },
    { name: "Within Free Time", value: withinFree, color: "#10B981" },
  ].filter((d) => d.value > 0);
  const bars = enriched.map((e) => ({ container: e.container, remaining: e.remaining, color: e.color }));

  return (
    <div data-testid="detention-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="text-sm text-muted-foreground">Empty-container detention & demurrage — {mode === "export" ? "export (empty pickup to gate-in at origin)" : "import (arrival to return)"}.</p>
        <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="detention-mode-select" className="bg-white border border-border rounded-md px-3 py-2 text-xs font-mono-plex tracking-widest uppercase">
          <option value="export">Export</option>
          <option value="import">Import</option>
        </select>
      </div>
      {atRisk > 0 && (
        <div className="border border-red-400/30 bg-red-400/10 px-4 py-3 mb-6 flex items-center gap-3" data-testid="detention-alert">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
          <span className="text-sm text-red-300"><span className="font-semibold">{atRisk} container{atRisk > 1 ? "s" : ""} need attention</span> — {onDetention} on detention, {dueToday} due today, {approaching} approaching free-time expiry.</span>
        </div>
      )}
      <div className="border-t border-l border-border grid grid-cols-2 lg:grid-cols-4 mb-8">
        {kpis.map((k, i) => (
          <div key={i} className="border-r border-b border-border bg-white p-6 relative" data-testid={`detention-kpi-${i}`}>
            <div className="absolute left-0 top-0 h-full w-1" style={{ background: k.color }} />
            <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-3">{k.label}</div>
            <div className="font-display text-4xl tracking-tight mb-2" style={{ color: k.color }}>{k.value}</div>
            <div className="text-[11px] text-muted-foreground">{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="border border-border bg-white p-6">
          <h3 className="text-base font-semibold tracking-tight">Status breakdown</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Containers by detention risk</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={breakdown} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={2} stroke="#FFFFFF">
                {breakdown.map((d, i) => (<Cell key={i} fill={d.color} />))}
              </Pie>
              <Tooltip contentStyle={tipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2 justify-center">
            {breakdown.map((d, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2" style={{ background: d.color }} />{d.name} ({d.value})</span>
            ))}
          </div>
        </div>
        <div className="border border-border bg-white p-6">
          <h3 className="text-base font-semibold tracking-tight">Days to detention by container</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Free days remaining — negative = overdue &amp; accruing</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bars} layout="vertical" margin={{ top: 5, right: 24, left: 24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis type="category" dataKey="container" stroke="#64748B" fontSize={9} tickLine={false} axisLine={false} width={95} />
              <Tooltip cursor={{ fill: "#64748B", fillOpacity: 0.08 }} contentStyle={tipStyle} formatter={(v) => [`${v} days`, "Remaining"]} />
              <ReferenceLine x={0} stroke="#3f3f46" />
              <Bar dataKey="remaining" radius={[0, 2, 2, 0]}>
                {bars.map((b, i) => (<Cell key={i} fill={b.color} />))}
                <LabelList dataKey="remaining" position="right" fill="#64748B" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {totalCharges > 0 && (
        <div className="text-sm text-muted-foreground mb-4">Estimated charges accrued to date: <span className="font-mono-plex text-red-400">US${totalCharges.toLocaleString()}</span></div>
      )}
      <Table head={["Container", "Port", "Type", "Free Days", "Days Used", "Days Left", "Demurrage", "Detention", "Status"]}>
        {enriched.map((r, i) => (
          <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors" data-testid={`detention-row-${i}`}>
            <td className="px-4 py-3 font-mono-plex text-foreground">{r.container}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.port}</td>
            <td className="px-4 py-3">{r.type}</td>
            <td className="px-4 py-3 font-mono-plex">{r.free_days}</td>
            <td className={`px-4 py-3 font-mono-plex ${r.days_used > r.free_days ? "text-red-400" : ""}`}>{r.days_used}</td>
            <td className="px-4 py-3 font-mono-plex" style={{ color: r.color }}>{r.remaining}</td>
            <td className="px-4 py-3 font-mono-plex">{r.demurrage}</td>
            <td className="px-4 py-3 font-mono-plex">{r.detention}</td>
            <td className="px-4 py-3"><Badge>{r.status}</Badge></td>
          </tr>
        ))}
      </Table>
    </div>
  );
};

const derive = (alloc, weeks) => {
  let backlog = 0;
  return weeks.map((w) => {
    const supply = backlog + w.demand;
    const loaded = Math.min(supply, alloc);
    const rollover = Math.max(supply - alloc, 0);
    backlog = rollover;
    const loadedCapped = Math.min(w.demand, alloc);
    const excess = Math.max(w.demand - alloc, 0);
    let color;
    if (w.planned) color = "#93C5FD";
    else if (w.demand > alloc) color = "#EF4444";
    else if (w.demand === alloc) color = "#1E40AF";
    else color = "#10B981";
    return { wk: w.wk, demand: w.demand, planned: !!w.planned, loaded, rollover, loadedCapped, excess, color };
  });
};

const tipStyle = { background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 12, color: "#0F172A" };

const SampleTag = () => (
  <span className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#F59E0B]/40 text-[#F59E0B] px-2 py-0.5" data-testid="sample-tag">Sample</span>
);

const RegionCard = ({ title, subtitle, children }) => (
  <div className="border border-border bg-white p-6">
    <div className="flex items-start justify-between mb-1">
      <h4 className="text-base font-semibold tracking-tight">{title}</h4>
      <span className="font-mono-plex text-[11px] text-muted-foreground">2026</span>
    </div>
    <p className="text-xs text-muted-foreground mb-6">{subtitle}</p>
    {children}
  </div>
);

const AllocationCharts = ({ data }) => {
  if (!data) return null;
  const ec = derive(data.east_coast.alloc, data.east_coast.weeks);
  const nz = derive(data.new_zealand.alloc, data.new_zealand.weeks);
  const ecA = data.east_coast.alloc;
  const nzA = data.new_zealand.alloc;
  const regions = [
    { title: "Export from Australia", rows: ec, alloc: ecA, top1: 16, top3: 20 },
    { title: "Export from New Zealand", rows: nz, alloc: nzA, top1: 16, top3: 20 },
  ];

  return (
    <div className="mt-14 space-y-14" data-testid="allocation-analysis">
      {/* 1. Loaded & Reserved capped at allocation */}
      <section data-testid="alloc-loaded">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <CheckCircle2 className="h-5 w-5 text-emerald-400" strokeWidth={1.75} />
          <h3 className="text-lg font-semibold tracking-tight">Loaded &amp; Reserved TEU — capped at allocation</h3>
          <SampleTag />
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground mb-2">
          <span className="font-mono-plex text-[10px] uppercase tracking-widest">Colour coding:</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#10B981]" />Under allocation — space available</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#1E40AF]" />At allocation — full</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#EF4444]" />Over allocation — carries forward</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-[#93C5FD]" />Faded = future Reserved/Planned</span>
        </div>
        <p className="text-xs text-muted-foreground mb-6"><span className="font-bold text-foreground">13</span> = bold figure above each bar is the true demand (loaded is capped at allocation)</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {regions.map((r, idx) => (
            <RegionCard key={idx} title={r.title} subtitle={`Loaded (past) & Reserved/Planned (future) TEU per sailing — allocation ${r.alloc} TEU/sailing`}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={r.rows} margin={{ top: 24, right: 10, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                  <XAxis dataKey="wk" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} angle={-35} textAnchor="end" height={45} interval={0} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[0, r.top1]} />
                  <Tooltip cursor={{ fill: "#64748B", fillOpacity: 0.08 }} contentStyle={tipStyle} formatter={(v, n, p) => [`${p.payload.demand} TEU demand · ${p.payload.loadedCapped} loaded`, p.payload.wk]} />
                  <ReferenceLine y={r.alloc} stroke="#3f3f46" strokeDasharray="4 4" />
                  <Bar dataKey="loadedCapped" radius={[2, 2, 0, 0]}>
                    {r.rows.map((d, i) => (<Cell key={i} fill={d.color} fillOpacity={d.planned ? 0.55 : 1} />))}
                    <LabelList dataKey="demand" position="top" fill="#0F172A" fontSize={12} fontWeight={700} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </RegionCard>
          ))}
        </div>
      </section>

      {/* 2. Excess demand over allocation */}
      <section data-testid="alloc-excess">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Layers className="h-5 w-5 text-[#EF4444]" strokeWidth={1.75} />
          <h3 className="text-lg font-semibold tracking-tight">Excess Demand Over Allocation (Per week)</h3>
          <SampleTag />
        </div>
        <p className="text-xs text-muted-foreground mb-6">Demand above each week's own allocation. This is standalone per week (not accumulated).</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {regions.map((r, idx) => (
            <RegionCard key={idx} title={r.title} subtitle={`Excess TEU over allocation — allocation ${r.alloc} TEU/sailing`}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={r.rows} margin={{ top: 10, right: 10, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                  <XAxis dataKey="wk" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} angle={-35} textAnchor="end" height={45} interval={0} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[0, 6]} />
                  <Tooltip cursor={{ fill: "#64748B", fillOpacity: 0.08 }} contentStyle={tipStyle} formatter={(v) => [`${v} TEU over`, "Excess"]} />
                  <Bar dataKey="excess" fill="#EF4444" radius={[2, 2, 0, 0]}>
                    {r.rows.map((d, i) => (<Cell key={i} fillOpacity={d.planned ? 0.55 : 1} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </RegionCard>
          ))}
        </div>
      </section>

      {/* 3. Cumulative rollover */}
      <section data-testid="alloc-rollover">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Repeat className="h-5 w-5 text-[#F59E0B]" strokeWidth={1.75} />
          <h3 className="text-lg font-semibold tracking-tight">Cumulative rollover — backlog rolls into next sailing</h3>
          <SampleTag />
        </div>
        <p className="text-xs text-muted-foreground mb-6">TEU that can't ship on a sailing is added to the next sailing's demand, so backlog compounds.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {regions.map((r, idx) => (
            <RegionCard key={idx} title={r.title} subtitle={`Loaded / Reserved (green) vs backlog rolled to next sailing (red) — allocation ${r.alloc} TEU/sailing`}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={r.rows} margin={{ top: 10, right: 10, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={1} vertical={false} />
                  <XAxis dataKey="wk" stroke="#64748B" fontSize={10} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} angle={-35} textAnchor="end" height={45} interval={0} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} domain={[0, r.top3]} />
                  <Tooltip cursor={{ fill: "#64748B", fillOpacity: 0.08 }} contentStyle={tipStyle} formatter={(v, n) => [`${v} TEU`, n === "loaded" ? "Loaded / Reserved" : "Backlog rolled"]} />
                  <ReferenceLine y={r.alloc} stroke="#3f3f46" strokeDasharray="4 4" />
                  <Bar dataKey="loaded" stackId="a">
                    {r.rows.map((d, i) => (<Cell key={i} fill="#10B981" fillOpacity={d.planned ? 0.5 : 1} />))}
                  </Bar>
                  <Bar dataKey="rollover" stackId="a" radius={[2, 2, 0, 0]}>
                    {r.rows.map((d, i) => (<Cell key={i} fill="#EF4444" fillOpacity={d.planned ? 0.5 : 1} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </RegionCard>
          ))}
        </div>
      </section>
    </div>
  );
};

const alertColor = (a) => {
  const v = String(a).toLowerCase();
  if (v.includes("expired") || v.includes("today")) return "text-red-400 border-red-400/30";
  if (v.includes("3 days") || v.includes("5 days")) return "text-amber-400 border-amber-400/30";
  return "text-emerald-400 border-emerald-400/30";
};

const DetentionReport = ({ report }) => {
  if (!report) return null;
  const cols = ["Client Code", "Job Ref", "Container #", "Inco", "Owners Ref", "Deliver To", "Mode", "Type", "Vessel", "Voyage", "ETA", "Free Days", "Last Free Date", "Days to Detention", "Days Past Empty", "Status", "Est. Detention Cost"];
  return (
    <div data-testid="detention-report">
      <div className="border border-border bg-white p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-plex text-[11px] tracking-widest uppercase text-[#F59E0B] mb-1">{report.company}</div>
            <h3 className="font-display text-2xl tracking-tight">{report.title}</h3>
          </div>
          <div className="text-xs text-muted-foreground font-mono-plex space-y-1">
            <div>Client: <span className="text-foreground">{report.client}</span></div>
            <div>Empty Un-returned: {report.empty_unreturned}</div>
            <div>Printed by: {report.printed_by}</div>
          </div>
        </div>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[1300px]">
          <thead>
            <tr className="bg-slate-50 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
              {cols.map((h, i) => (<th key={i} className="text-left font-normal px-3 py-3 whitespace-nowrap">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r, i) => (
              <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors" data-testid={`detention-report-row-${i}`}>
                <td className="px-3 py-3 font-mono-plex">{r.client_code}</td>
                <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.job_ref}</td>
                <td className="px-3 py-3 font-mono-plex text-foreground">{r.container}</td>
                <td className="px-3 py-3">{r.inco_term}</td>
                <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.owners_ref}</td>
                <td className="px-3 py-3 text-muted-foreground">{r.deliver_to}</td>
                <td className="px-3 py-3">{r.cont_mode}</td>
                <td className="px-3 py-3">{r.cont_type}</td>
                <td className="px-3 py-3">{r.vessel}</td>
                <td className="px-3 py-3 font-mono-plex">{r.voyage}</td>
                <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.eta}</td>
                <td className="px-3 py-3 font-mono-plex">{r.free_days}</td>
                <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.last_free_date}</td>
                <td className="px-3 py-3 font-mono-plex">{r.days_to_detention_start || "—"}</td>
                <td className={`px-3 py-3 font-mono-plex ${r.days_past_empty_return ? "text-red-400" : ""}`}>{r.days_past_empty_return || "—"}</td>
                <td className="px-3 py-3"><span className={`inline-block font-mono-plex text-[10px] uppercase tracking-widest border px-2 py-0.5 whitespace-nowrap ${alertColor(r.alert)}`}>{r.alert}</span></td>
                <td className="px-3 py-3 font-mono-plex text-foreground">{r.est_cost || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground/50 mt-4 font-mono-plex tracking-widest uppercase">Sample — Container Detention Monitoring Report</p>
    </div>
  );
};

const ShipmentTrackingReport = ({ report }) => (
  <div data-testid="shipment-tracking-report">
    <div className="mb-6">
      <h3 className="text-lg font-semibold tracking-tight">{report.title}</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-3xl">{report.subtitle}</p>
    </div>
    <div className="border border-border overflow-x-auto">
      <table className="w-full text-sm min-w-[1100px]">
        <thead>
          <tr className="bg-slate-50 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
            {["Order Ref", "Shipment", "House", "Shipper", "Mode", "Origin", "Dest", "ETD", "ETA", "ATA", "Container #", "Depart Vessel", "Voyage", "Job Notes"].map((h, i) => (
              <th key={i} className="text-left font-normal px-3 py-3 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((r, i) => (
            <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors" data-testid={`shipment-row-${i}`}>
              <td className="px-3 py-3 font-mono-plex text-foreground">{r.order_ref}</td>
              <td className="px-3 py-3 font-mono-plex">{r.shipment}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.house}</td>
              <td className="px-3 py-3">{r.shipper}</td>
              <td className="px-3 py-3 font-mono-plex">{r.mode}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.origin}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.dest}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.etd}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.eta}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.ata || "—"}</td>
              <td className="px-3 py-3 font-mono-plex">{r.container}</td>
              <td className="px-3 py-3">{r.depart_vessel}</td>
              <td className="px-3 py-3 font-mono-plex text-muted-foreground">{r.dep_voy}</td>
              <td className="px-3 py-3 text-muted-foreground">{r.notes || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-[11px] text-muted-foreground/50 mt-4 font-mono-plex tracking-widest uppercase">Sample — {report.printed_by}</p>
  </div>
);

const CO2_COLORS = ["#0B6E4F", "#2E9E6B", "#8DC63F", "#C8E6A0"];

const Co2Report = ({ report }) => {
  const s = report.summary;
  const kpis = [
    { label: "Total CO2e", value: `${s.co2e_tons} t`, sub: "well-to-wheel" },
    { label: "Shipments", value: s.shipments, sub: `${s.teu} TEU` },
    { label: "Actual Weight", value: `${s.weight_tons.toLocaleString()} t`, sub: "moved" },
    { label: "Carbon Intensity", value: `${s.co2e_per_tkm}`, sub: "g CO2e / t-km" },
  ];
  const modes = report.modes.filter((m) => m.co2e > 0);
  return (
    <div data-testid="co2-report">
      <div className="mb-6">
        <h3 className="text-lg font-semibold tracking-tight flex items-center gap-2"><Leaf className="h-5 w-5 text-[#0B6E4F]" strokeWidth={1.5} />{report.title}</h3>
        <p className="text-xs text-muted-foreground mt-1">{report.period} · {report.standards}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k, i) => (
          <div key={i} className="border border-border bg-white p-5" data-testid={`co2-kpi-${i}`}>
            <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{k.label}</div>
            <div className="text-2xl font-semibold tracking-tight text-foreground">{k.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-border bg-white p-6">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-4">CO2e by Transport Mode (tons)</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={modes} dataKey="co2e" nameKey="mode" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.mode} ${e.co2e}t`}>
                {modes.map((m, i) => <Cell key={i} fill={CO2_COLORS[i % CO2_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${v} t`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border bg-white p-6">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-4">CO2e — Last 12 Months (tons)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={report.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => `${v} t`} />
              <Bar dataKey="co2e" fill="#2E9E6B" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="border border-border bg-white p-6 mb-6">
        <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-4">CO2e by Trade Lane (tons, well-to-wheel)</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={report.tradelanes} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="lane" width={150} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => `${v} t`} />
            <Bar dataKey="co2e" fill="#0B6E4F" radius={[0, 3, 3, 0]}>
              <LabelList dataKey="co2e" position="right" formatter={(v) => `${v}t`} style={{ fontSize: 11, fill: "#0B6E4F" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="border border-border bg-white p-6">
        <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Other Emissions & Activity (year to date)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {[
            { label: "NOx", value: `${report.pollutants.nox_kg.toLocaleString()} kg` },
            { label: "SOx", value: `${report.pollutants.sox_kg.toLocaleString()} kg` },
            { label: "PM10", value: `${report.pollutants.pm10_kg.toLocaleString()} kg` },
            { label: "Tonne-Km", value: report.pollutants.tonne_km.toLocaleString() },
            { label: "Distance", value: `${report.pollutants.distance_km.toLocaleString()} km` },
          ].map((p, i) => (
            <div key={i}>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{p.label}</div>
              <div className="text-base font-semibold text-foreground">{p.value}</div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground/50 mt-4 font-mono-plex tracking-widest uppercase">Sample — CO2e Emissions Report 2025</p>
    </div>
  );
};

const REPORT_TYPES = [
  { key: "detention", label: "Container Detention Monitoring Report", desc: "Full delivery & empty return, detention exposure", icon: Timer },
  { key: "orders", label: "Orders Report", desc: "Open purchase orders by lane & status", icon: Package },
  { key: "shipment", label: "Shipment Tracking Report", desc: "Shipment listing — vessels, ETD/ETA & milestones", icon: Ship },
  { key: "co2", label: "CO2 Report", desc: "CO2e emissions by mode & trade lane", icon: Leaf },
];

const csvEscape = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename, headers, rows) => {
  const lines = [headers.map(csvEscape).join(",")];
  rows.forEach((r) => lines.push(r.map(csvEscape).join(",")));
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const buildReportCSV = (key, data) => {
  if (key === "detention") {
    const headers = ["Client Code", "Job Ref", "Container #", "Inco", "Owners Ref", "Deliver To", "Mode", "Type", "Vessel", "Voyage", "ETA", "Free Days", "Last Free Date", "Days to Detention", "Days Past Empty", "Status", "Est. Detention Cost"];
    const rows = (data.detention_report?.rows || []).map((r) => [r.client_code, r.job_ref, r.container, r.inco_term, r.owners_ref, r.deliver_to, r.cont_mode, r.cont_type, r.vessel, r.voyage, r.eta, r.free_days, r.last_free_date, r.days_to_detention_start, r.days_past_empty_return, r.alert, r.est_cost]);
    return { filename: "container-detention-monitoring-report.csv", headers, rows };
  }
  if (key === "orders") {
    const headers = ["Booking #", "Consignee", "Origin", "Destination", "Containers", "Incoterm", "Status", "ETA"];
    const rows = (data.orders || []).map((r) => [r.po, r.supplier, r.origin, r.destination, r.containers, r.incoterm, r.status, r.eta]);
    return { filename: "orders-report.csv", headers, rows };
  }
  if (key === "shipment") {
    const headers = ["Order Ref", "Shipment", "House", "Shipper", "Mode", "Origin", "Dest", "ETD", "ETA", "ATA", "Container #", "Depart Vessel", "Voyage", "Job Notes"];
    const rows = (data.shipment_tracking?.rows || []).map((r) => [r.order_ref, r.shipment, r.house, r.shipper, r.mode, r.origin, r.dest, r.etd, r.eta, r.ata, r.container, r.depart_vessel, r.dep_voy, r.notes]);
    return { filename: "shipment-tracking-report.csv", headers, rows };
  }
  // co2
  const rep = data.co2_report;
  const headers = ["Section", "Item", "Value", "Unit"];
  const rows = [];
  const s = rep.summary;
  rows.push(["Summary", "Total CO2e", s.co2e_tons, "t"]);
  rows.push(["Summary", "Total CO2", s.co2_tons, "t"]);
  rows.push(["Summary", "Shipments", s.shipments, ""]);
  rows.push(["Summary", "TEU", s.teu, ""]);
  rows.push(["Summary", "Actual Weight", s.weight_tons, "t"]);
  rows.push(["Summary", "Carbon Intensity", s.co2e_per_tkm, "g CO2e/t-km"]);
  rep.modes.forEach((m) => rows.push(["By Mode", m.mode, m.co2e, "t"]));
  rep.tradelanes.forEach((t) => rows.push(["By Trade Lane", t.lane, t.co2e, "t"]));
  rep.monthly.forEach((m) => rows.push(["Monthly", m.month, m.co2e, "t"]));
  const p = rep.pollutants;
  rows.push(["Other Emissions", "NOx", p.nox_kg, "kg"]);
  rows.push(["Other Emissions", "SOx", p.sox_kg, "kg"]);
  rows.push(["Other Emissions", "PM10", p.pm10_kg, "kg"]);
  rows.push(["Activity", "Tonne-Km", p.tonne_km, "t-km"]);
  rows.push(["Activity", "Distance", p.distance_km, "km"]);
  return { filename: "co2-emissions-report.csv", headers, rows };
};

const chargeStatusColor = (s) => {
  const v = String(s).toLowerCase();
  if (v.includes("approved")) return "text-emerald-600 border-emerald-600/30";
  if (v.includes("rejected")) return "text-red-500 border-red-500/30";
  if (v.includes("pending")) return "text-amber-600 border-amber-600/30";
  return "text-muted-foreground border-border";
};

const aud = (n) => `A$${Math.round(n).toLocaleString()}`;

const AdditionalCharges = ({ audit }) => {
  const [rows, setRows] = useState(audit ? audit.rows : []);
  if (!audit) return null;
  const setStatus = (i, status) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, status } : r)));
    toast[status === "Approved" ? "success" : "message"](`Charge ${status.toLowerCase()}`, { description: `${rows[i].request_no} · ${rows[i].charge_type} · ${aud(rows[i].amount_aud)}` });
  };
  const pending = rows.filter((r) => r.status === "Pending Approval");
  const approvedAmt = rows.filter((r) => r.status === "Approved").reduce((a, r) => a + r.amount_aud, 0);
  const pendingAmt = pending.reduce((a, r) => a + r.amount_aud, 0);
  const kpis = [
    { label: "Awaiting ARB Approval", value: pending.length, sub: `${aud(pendingAmt)} pending` },
    { label: "Approved", value: rows.filter((r) => r.status === "Approved").length, sub: `${aud(approvedAmt)} authorised` },
    { label: "Rejected", value: rows.filter((r) => r.status === "Rejected").length, sub: "charges withdrawn" },
    { label: "Total Requests", value: rows.length, sub: "additional-charge approvals" },
  ];
  return (
    <div data-testid="additional-charges">
      <div className="flex items-start gap-3 border border-[#E3000F]/25 bg-[#E3000F]/[0.04] p-5 mb-8" data-testid="charge-policy">
        <AlertTriangle className="h-5 w-5 text-[#E3000F] shrink-0 mt-0.5" strokeWidth={1.75} />
        <div>
          <div className="font-mono-plex text-[10px] tracking-widest uppercase text-[#E3000F] mb-1">Approval Policy</div>
          <p className="text-sm text-foreground leading-relaxed max-w-3xl">{audit.policy}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k, i) => (
          <div key={i} className="border border-border bg-white p-5" data-testid={`charge-kpi-${i}`}>
            <div className="font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{k.label}</div>
            <div className="text-2xl font-semibold tracking-tight text-foreground">{k.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <thead>
            <tr className="bg-slate-50 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
              {["Request #", "Charge Type", "Job Ref", "Container", "Destination", "Reason", "Amount", "Status", "Action"].map((h, i) => (
                <th key={i} className={`font-normal px-4 py-3 whitespace-nowrap ${h === "Amount" ? "text-right" : h === "Action" ? "text-center" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border bg-white hover:bg-slate-50 transition-colors align-top" data-testid={`charge-row-${i}`}>
                <td className="px-4 py-3 font-mono-plex text-foreground">{r.request_no}</td>
                <td className="px-4 py-3 font-medium">{r.charge_type}</td>
                <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.job_ref}</td>
                <td className="px-4 py-3 font-mono-plex text-muted-foreground">{r.container}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.destination}</td>
                <td className="px-4 py-3 text-muted-foreground max-w-[260px]">{r.reason}</td>
                <td className="px-4 py-3 font-mono-plex text-right text-foreground">{aud(r.amount_aud)}</td>
                <td className="px-4 py-3"><span className={`inline-block font-mono-plex text-[10px] uppercase tracking-widest border px-2 py-0.5 whitespace-nowrap ${chargeStatusColor(r.status)}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-center whitespace-nowrap">
                  {r.status === "Pending Approval" ? (
                    <div className="inline-flex items-center gap-2">
                      <button data-testid={`charge-approve-${i}`} onClick={() => setStatus(i, "Approved")} className="inline-flex items-center gap-1 border border-emerald-600/40 text-emerald-700 px-3 py-1.5 text-[11px] font-mono-plex uppercase tracking-widest hover:bg-emerald-50 transition-colors">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button data-testid={`charge-reject-${i}`} onClick={() => setStatus(i, "Rejected")} className="inline-flex items-center gap-1 border border-red-500/40 text-red-600 px-3 py-1.5 text-[11px] font-mono-plex uppercase tracking-widest hover:bg-red-50 transition-colors">
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-muted-foreground/50 font-mono-plex text-[10px] uppercase tracking-widest">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Reports = ({ data }) => {
  const [report, setReport] = useState("detention");
  const handleExport = () => {
    const { filename, headers, rows } = buildReportCSV(report, data);
    downloadCSV(filename, headers, rows);
    toast.success("CSV exported", { description: filename });
  };
  return (
    <div data-testid="reports-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {REPORT_TYPES.map((rt) => {
          const Icon = rt.icon;
          const on = report === rt.key;
          return (
            <button key={rt.key} data-testid={`report-card-${rt.key}`} onClick={() => setReport(rt.key)} className={`text-left border p-5 transition-colors ${on ? "border-[#F59E0B] bg-slate-50" : "border-border bg-white hover:border-muted-foreground/40"}`}>
              <Icon className={`h-5 w-5 mb-3 ${on ? "text-[#F59E0B]" : "text-muted-foreground"}`} strokeWidth={1.5} />
              <div className="text-sm font-semibold leading-snug mb-1">{rt.label}</div>
              <div className="text-xs text-muted-foreground">{rt.desc}</div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-end mb-4">
        <button data-testid="export-csv-button" onClick={handleExport} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2 text-xs font-mono-plex uppercase tracking-widest hover:border-[#E3000F] hover:text-[#E3000F] transition-colors">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      {report === "detention" && <DetentionReport report={data.detention_report} />}
      {report === "orders" && <OrdersTable rows={data.orders} />}
      {report === "shipment" && <ShipmentTrackingReport report={data.shipment_tracking} />}
      {report === "co2" && <Co2Report report={data.co2_report} />}
    </div>
  );
};

const UploadOrders = ({ onUploaded, source }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/supply-chain/upload", fd);
      toast.success("Order file uploaded", { description: `${f.name} — graphs updated (${data.regions.map((r) => r.replace("_", " ")).join(", ")})` });
      onUploaded();
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="border border-dashed border-border bg-white p-6 mb-8" data-testid="upload-orders">
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Upload className="h-4 w-4 text-[#F59E0B]" />
            <h3 className="text-base font-semibold tracking-tight">Upload Order file</h3>
          </div>
          <p className="text-xs text-muted-foreground max-w-xl leading-relaxed">
            Upload an <span className="text-foreground">.xls / .xlsx / .csv</span> with columns <span className="font-mono-plex text-foreground">Region, Week, Demand, Allocation</span> (optional <span className="font-mono-plex">Planned</span>). The allocation graphs below update automatically.
          </p>
          {source && <p className="text-[11px] text-muted-foreground/60 mt-2 font-mono-plex">Current source: {source}</p>}
        </div>
        <div>
          <input ref={inputRef} type="file" accept=".xls,.xlsx,.csv" onChange={onFile} className="hidden" data-testid="order-file-input" />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="upload-orders-button"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Choose file"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function SupplyChain() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = TABS.find((t) => t.key === tab) ? tab : "business-review";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/supply-chain").then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-6 py-10" data-testid="supply-chain-page">
        <div className="mb-8 animate-fade-up">
          <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Supply Chain</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{TABS.find((t) => t.key === active).label}</h1>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-border mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === active;
            return (
              <button
                key={t.key}
                data-testid={`subtab-${t.key}`}
                onClick={() => navigate(`/supply-chain/${t.key}`)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${on ? "border-[#F59E0B] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
              </button>
            );
          })}
        </div>

        {["business-review", "account", "operational"].includes(active) && (
          <div data-testid={`panel-${active}`}>
            {active === "business-review" && <BusinessReview />}
            {active === "account" && <AccountStructure />}
            {active === "operational" && <OperationalRelated />}
          </div>
        )}

        {!["business-review", "account", "operational"].includes(active) && (
          <>
            {loading && <div className="font-mono-plex text-xs tracking-widest text-muted-foreground uppercase animate-pulse">Loading…</div>}

            {error && (
              <div className="flex flex-col items-start gap-3" data-testid="supply-error">
                <p className="text-sm text-muted-foreground">Couldn't load supply chain data.</p>
                <button onClick={load} className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:border-muted-foreground/40 transition-colors">Retry</button>
              </div>
            )}

            {data && !loading && !error && (
              <div data-testid={`panel-${active}`}>
                {active === "orders" && (
                  <>
                    <UploadOrders onUploaded={load} source={data.allocation_source} />
                    <OrdersTable rows={data.orders} />
                    <AllocationCharts data={data.allocation} />
                  </>
                )}
                {active === "schedules" && <SchedulesTable rows={data.schedules} />}
                {active === "tracking" && <Tracking rows={data.tracking} />}
                {active === "detention" && <Detention data={data.detention} />}
                {active === "reports" && <Reports data={data} />}
                {active === "invoice" && <AdditionalCharges audit={data.additional_charges} />}
                <p className="text-[11px] text-muted-foreground/50 mt-6 font-mono-plex tracking-widest uppercase">Sample data shown for demonstration purposes</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
